// MathFormulaEditor — Editor inline de fórmulas matemáticas (KaTeX + LaTeX).
//
// El docente puede insertar fórmulas dentro del enunciado de la pregunta:
//   - Hace clic en "Insertar fórmula" donde tenga el cursor
//   - Se abre un editor con LaTeX + barra de símbolos categorizada
//   - Al guardar se inserta como un placeholder `[[math:...]]` en el texto
//   - Al renderizar (preview / estudiante), `[[math:...]]` se reemplaza por
//     la fórmula renderizada con KaTeX.
//
// Estudiante: puede usar el mismo componente para CONSTRUIR su respuesta
// matemática visualmente.
import { useState, Component } from "react";
import { BlockMath, InlineMath } from "react-katex";
import "katex/dist/katex.min.css";
import { X, Sigma, Plus } from "lucide-react";

// Catálogo de símbolos / plantillas LaTeX por categoría.
export const MATH_PALETTES = {
  basicas: {
    label: "Básicas",
    items: [
      { latex: "+", display: "+" },
      { latex: "-", display: "−" },
      { latex: "\\times", display: "×" },
      { latex: "\\div", display: "÷" },
      { latex: "=", display: "=" },
      { latex: "\\neq", display: "≠" },
      { latex: "<", display: "<" },
      { latex: ">", display: ">" },
      { latex: "\\leq", display: "≤" },
      { latex: "\\geq", display: "≥" },
      { latex: "\\pm", display: "±" },
      { latex: "\\cdot", display: "·" },
      { latex: "\\frac{a}{b}", display: "a/b" },
      { latex: "\\sqrt{x}", display: "√x" },
      { latex: "x^{2}", display: "x²" },
      { latex: "x^{n}", display: "xⁿ" },
      { latex: "x_{i}", display: "xᵢ" },
      { latex: "(", display: "(" },
      { latex: ")", display: ")" },
      { latex: "\\%", display: "%" },
      { latex: "\\infty", display: "∞" },
    ],
  },
  algebra: {
    label: "Álgebra",
    items: [
      { latex: "\\sum_{i=1}^{n}", display: "Σ" },
      { latex: "\\prod_{i=1}^{n}", display: "∏" },
      { latex: "\\lim_{x \\to a}", display: "lim" },
      { latex: "\\log_{b}(x)", display: "log_b" },
      { latex: "\\ln(x)", display: "ln" },
      { latex: "e^{x}", display: "eˣ" },
      { latex: "f(x)", display: "f(x)" },
      { latex: "f^{-1}(x)", display: "f⁻¹" },
      { latex: "\\binom{n}{k}", display: "C(n,k)" },
      { latex: "\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}", display: "matriz" },
      { latex: "\\vec{v}", display: "vec v" },
      { latex: "\\overrightarrow{AB}", display: "→AB" },
      { latex: "|x|", display: "|x|" },
      { latex: "\\lfloor x \\rfloor", display: "⌊x⌋" },
      { latex: "\\lceil x \\rceil", display: "⌈x⌉" },
    ],
  },
  calculo: {
    label: "Cálculo",
    items: [
      { latex: "\\int", display: "∫" },
      { latex: "\\int_{a}^{b}", display: "∫_a^b" },
      { latex: "\\oint", display: "∮" },
      { latex: "\\iint", display: "∬" },
      { latex: "\\frac{d}{dx}", display: "d/dx" },
      { latex: "\\frac{\\partial}{\\partial x}", display: "∂/∂x" },
      { latex: "\\nabla", display: "∇" },
      { latex: "dx", display: "dx" },
      { latex: "dy", display: "dy" },
      { latex: "\\Delta", display: "Δ" },
      { latex: "\\delta", display: "δ" },
      { latex: "\\epsilon", display: "ε" },
    ],
  },
  geometria: {
    label: "Geometría",
    items: [
      { latex: "\\pi", display: "π" },
      { latex: "\\theta", display: "θ" },
      { latex: "\\alpha", display: "α" },
      { latex: "\\beta", display: "β" },
      { latex: "\\gamma", display: "γ" },
      { latex: "^{\\circ}", display: "°" },
      { latex: "\\angle", display: "∠" },
      { latex: "\\triangle", display: "△" },
      { latex: "\\perp", display: "⊥" },
      { latex: "\\parallel", display: "∥" },
      { latex: "\\cong", display: "≅" },
      { latex: "\\sim", display: "~" },
      { latex: "\\sin(x)", display: "sin" },
      { latex: "\\cos(x)", display: "cos" },
      { latex: "\\tan(x)", display: "tan" },
    ],
  },
  fisica: {
    label: "Física",
    items: [
      { latex: "F = ma", display: "F=ma" },
      { latex: "E = mc^{2}", display: "E=mc²" },
      { latex: "v = \\frac{d}{t}", display: "v=d/t" },
      { latex: "a = \\frac{\\Delta v}{\\Delta t}", display: "a=Δv/Δt" },
      { latex: "p = mv", display: "p=mv" },
      { latex: "E_{k} = \\frac{1}{2}mv^{2}", display: "Ek" },
      { latex: "E_{p} = mgh", display: "Ep" },
      { latex: "W = F \\cdot d", display: "W=F·d" },
      { latex: "\\omega", display: "ω" },
      { latex: "\\lambda", display: "λ" },
      { latex: "\\mu", display: "μ" },
      { latex: "\\rho", display: "ρ" },
      { latex: "\\hbar", display: "ℏ" },
      { latex: "\\vec{F}", display: "F⃗" },
      { latex: "\\hat{n}", display: "n̂" },
    ],
  },
  conjuntos: {
    label: "Conjuntos / Lógica",
    items: [
      { latex: "\\in", display: "∈" },
      { latex: "\\notin", display: "∉" },
      { latex: "\\subset", display: "⊂" },
      { latex: "\\subseteq", display: "⊆" },
      { latex: "\\cup", display: "∪" },
      { latex: "\\cap", display: "∩" },
      { latex: "\\emptyset", display: "∅" },
      { latex: "\\forall", display: "∀" },
      { latex: "\\exists", display: "∃" },
      { latex: "\\neg", display: "¬" },
      { latex: "\\land", display: "∧" },
      { latex: "\\lor", display: "∨" },
      { latex: "\\Rightarrow", display: "⇒" },
      { latex: "\\Leftrightarrow", display: "⇔" },
      { latex: "\\therefore", display: "∴" },
    ],
  },
};

/**
 * MathFormulaModal
 * Modal con LaTeX editable + barra de símbolos categorizada + live preview.
 * Devuelve el LaTeX a `onConfirm(latex)`.
 */
export function MathFormulaModal({ initial = "", onClose, onConfirm }) {
  const [latex, setLatex] = useState(initial);
  const [palette, setPalette] = useState("basicas");
  const [error, setError] = useState(null);

  const insert = (snippet) => setLatex((prev) => `${prev}${prev ? " " : ""}${snippet}`);

  return (
    <div className="fixed inset-0 z-[60] bg-[#1F2547]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="math-formula-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E1E5F0] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sigma size={18} className="text-[#A43790]" />
            <div>
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-[#A43790]">Editor de fórmulas</p>
              <h3 className="font-display text-lg font-extrabold text-[#1F2547]">Insertar fórmula matemática</h3>
            </div>
          </div>
          <button onClick={onClose} className="text-[#6E7591] hover:text-[#1F2547]" data-testid="math-modal-close"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Tabs categorías */}
          <div className="flex flex-wrap gap-1.5" data-testid="math-palettes">
            {Object.entries(MATH_PALETTES).map(([key, p]) => (
              <button key={key} type="button" onClick={() => setPalette(key)}
                data-testid={`math-palette-${key}`}
                className={`text-[11px] font-bold px-3 py-1 rounded-full border-2 ${palette === key ? "bg-[#1F2547] border-[#1F2547] text-white" : "bg-white border-[#E1E5F0] text-[#4C5478] hover:border-[#A43790]"}`}>
                {p.label}
              </button>
            ))}
          </div>

          {/* Símbolos */}
          <div className="grid grid-cols-6 sm:grid-cols-10 gap-1.5 bg-[#F7F8FA] rounded-xl p-2 border border-[#E1E5F0]">
            {MATH_PALETTES[palette].items.map((it, i) => (
              <button key={i} type="button" onClick={() => insert(it.latex)}
                title={it.latex}
                data-testid={`math-symbol-${palette}-${i}`}
                className="min-h-[36px] px-2 py-1 rounded-lg border-2 border-[#E1E5F0] bg-white hover:border-[#A43790] hover:bg-[#FFF1F8] text-sm font-bold text-[#1F2547]">
                <span style={{ fontSize: it.display.length > 4 ? "10px" : "13px" }}>{it.display}</span>
              </button>
            ))}
          </div>

          {/* LaTeX */}
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider font-extrabold text-[#6E7591]">LaTeX</span>
            <textarea
              value={latex}
              onChange={(e) => { setLatex(e.target.value); setError(null); }}
              rows={3}
              data-testid="math-latex-input"
              className="w-full mt-1 px-3 py-2 rounded-lg border-2 border-[#E1E5F0] font-mono text-sm focus:border-[#A43790] outline-none"
              placeholder="\\frac{a}{b} + \\sqrt{x^2 + y^2}"
            />
          </label>

          {/* Preview */}
          <div className="rounded-xl border-2 border-[#E1E5F0] bg-white p-4 min-h-[80px] flex items-center justify-center" data-testid="math-preview">
            {latex ? (
              <ErrorBoundaryMath onError={setError}>
                <BlockMath math={latex} />
              </ErrorBoundaryMath>
            ) : (
              <p className="text-[11px] text-[#6E7591] italic">Escribe LaTeX o usa la barra de símbolos para previsualizar.</p>
            )}
          </div>
          {error && (
            <p className="text-[11px] font-bold text-[#A52258] bg-[#FFE9F0] border border-[#A43790] rounded px-2 py-1">⚠ {String(error)}</p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E1E5F0] bg-[#F7F8FA] flex items-center gap-3">
          <button onClick={onClose} className="jasq-btn-ghost" data-testid="math-cancel">Cancelar</button>
          <div className="flex-1" />
          <button onClick={() => onConfirm(latex)} disabled={!latex.trim() || !!error}
            className="jasq-btn-primary inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="math-confirm">
            <Plus size={14} /> Insertar fórmula
          </button>
        </div>
      </div>
    </div>
  );
}

// Captura errores de katex para mostrar mensaje amigable sin romper toda la UI.
class ErrorBoundaryMath extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err) { this.props.onError?.(err.message || String(err)); }
  componentDidUpdate(prevProps) {
    if (prevProps.children !== this.props.children) this.setState({ err: null });
  }
  render() {
    if (this.state.err) {
      return <p className="text-[11px] text-[#A52258] italic">Error de LaTeX: {this.state.err}</p>;
    }
    return this.props.children;
  }
}

/**
 * MathInline / MathBlock — renderizado.
 * Convierte un texto con tokens [[math:LATEX]] en JSX intercalando texto y
 * fórmulas KaTeX. Devuelve un fragmento React listo para usar.
 */
// Coincidencia perezosa: permite `]` internos (p. ej. \sqrt[3]{...}) y se
// detiene en el primer cierre `]]`. (#23 — antes [^\]]+ rompía con \sqrt[n].)
const MATH_TOKEN_RE = /\[\[math:([\s\S]*?)\]\]/g;

export function RenderTextWithMath({ text, displayMode = false, className = "" }) {
  if (!text) return null;
  const parts = [];
  let last = 0;
  let m;
  while ((m = MATH_TOKEN_RE.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    parts.push({ type: "math", value: m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });
  MATH_TOKEN_RE.lastIndex = 0;
  // Renderiza un trozo de texto plano respetando saltos de línea (\n → <br/>)
  // y preservando los espacios consecutivos para que los párrafos del docente
  // se vean tal cual los escribió.
  const renderTextNode = (value, key) => {
    if (!value) return null;
    const lines = value.split("\n");
    return (
      <span key={key} style={{ whiteSpace: "pre-wrap" }}>
        {lines.map((ln, idx) => (
          <span key={idx}>
            {ln}
            {idx < lines.length - 1 && <br />}
          </span>
        ))}
      </span>
    );
  };
  return (
    <span className={className}>
      {parts.map((p, i) => {
        if (p.type === "math") {
          // Envolvemos en ErrorBoundaryMath: react-katex lanza el error durante
          // el render del hijo, por lo que un try/catch síncrono NO lo captura.
          // Sin este boundary, un LaTeX inválido tumbaba toda la sala en vivo.
          return (
            <ErrorBoundaryMath key={i}>
              {displayMode
                ? <BlockMath math={p.value} />
                : <InlineMath math={p.value} />}
            </ErrorBoundaryMath>
          );
        }
        return renderTextNode(p.value, i);
      })}
    </span>
  );
}
